import { useState, useEffect, useRef } from 'react';
import api from '../api';
import { Radio, Plus, Pencil, Trash2, X, RefreshCw, Activity } from 'lucide-react';
import toast from 'react-hot-toast';
import { brand } from '../brand';

const ACCENT = brand?.colors?.accent || '#f0bb53';
const accentRgba = (a) => {
  const h = (ACCENT || '#f0bb53').replace('#', '');
  const n = h.length === 3 ? h.split('').map((x) => x + x).join('') : h;
  const r = parseInt(n.slice(0, 2), 16) || 0, g = parseInt(n.slice(2, 4), 16) || 0, b = parseInt(n.slice(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const TYPES = ['entrance', 'table', 'zone', 'restaurant', 'cage'];
const TYPE_COLORS = {
  entrance: 'bg-green-500/20 text-green-300',
  table: 'bg-blue-500/20 text-blue-300',
  zone: 'bg-amber-500/20 text-amber-300',
  restaurant: 'bg-pink-500/20 text-pink-300',
  cage: 'bg-purple-500/20 text-purple-300',
};
const WINDOWS = [
  { label: 'Last 15 min', minutes: 15 },
  { label: 'Last hour', minutes: 60 },
  { label: 'Last 4 hours', minutes: 240 },
  { label: 'Today', minutes: 24 * 60 },
];

const EMPTY = {
  uuid: '', major: '', minor: '', label: '', type: 'zone',
  linkedTableId: '', zoneName: '', floorX: '', floorY: '', txPower: '', isActive: true,
  notifyEnabled: false, notifyTitle: '', notifyBody: '', notifyMinProximity: 'near', notifyCooldownMinutes: '240',
  vipCooldownMinutes: '', positioningMode: 'full', coverageFloorX: '', coverageFloorY: '',
};
const PROXIMITIES = ['immediate', 'near', 'far'];

export default function Beacons() {
  const [tab, setTab] = useState('beacons');
  const [beacons, setBeacons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [calibrating, setCalibrating] = useState(false);

  // Auto-calibrate txPower from the beacon's recent sightings (median RSSI).
  // Workflow: stand ~1m from the beacon with the app open ~30s, then click.
  const handleCalibrate = async () => {
    if (!editing) return;
    setCalibrating(true);
    try {
      const res = await api.post(`/admin/beacons/${editing.id}/calibrate`);
      const tp = res.data?.txPower;
      if (tp != null) {
        setForm((f) => ({ ...f, txPower: String(tp) }));
        toast.success(`Calibrated: ${tp} dBm @1m${res.data.sampleCount ? ` (${res.data.sampleCount} samples)` : ''}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Calibration failed');
    } finally {
      setCalibrating(false);
    }
  };

  const [heatmap, setHeatmap] = useState(null);
  const [windowMin, setWindowMin] = useState(60);
  const [heatView, setHeatView] = useState('bars');
  const [mapMode, setMapMode] = useState('density'); // density | place
  const [placeId, setPlaceId] = useState('');
  const [floorPlanInput, setFloorPlanInput] = useState('');

  const placeBeacon = async (id, x, y) => {
    try {
      await api.put(`/admin/beacons/${id}`, { floorX: Number(x.toFixed(4)), floorY: Number(y.toFixed(4)) });
      const label = (heatmap?.beacons || []).find((b) => b.id === id)?.label || 'Beacon';
      toast.success(`${label} placed ✓`, { duration: 1500 });
      fetchHeatmap(windowMin);
    } catch (e) {
      toast.error('Failed to place beacon');
    }
  };

  // Set the "coverage target" for a coverage-only beacon: the walkable spot its
  // density should be attributed to (instead of its physical corner mount).
  const placeCoverageTarget = async (id, x, y) => {
    try {
      await api.put(`/admin/beacons/${id}`, { coverageFloorX: Number(x.toFixed(4)), coverageFloorY: Number(y.toFixed(4)) });
      const label = (heatmap?.beacons || []).find((b) => b.id === id)?.label || 'Beacon';
      toast.success(`${label} coverage target set ✓`, { duration: 1500 });
      fetchHeatmap(windowMin);
    } catch (e) {
      toast.error('Failed to set coverage target');
    }
  };

  const [positions, setPositions] = useState([]);
  const fetchPositions = async () => {
    try {
      const res = await api.get('/admin/beacons/positions', { params: { minutes: 3 } });
      setPositions(res.data.positions || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => {
    if (!(tab === 'heatmap' && heatView === 'map' && mapMode === 'positions')) return undefined;
    fetchPositions();
    const t = setInterval(fetchPositions, 8000); // live-ish
    return () => clearInterval(t);
  }, [tab, heatView, mapMode]);

  const saveFloorPlan = async () => {
    try {
      await api.put('/admin/beacons/floor-plan', { url: floorPlanInput.trim() });
      toast.success('Floor plan saved');
      fetchHeatmap(windowMin);
    } catch (e) {
      toast.error('Failed to save floor plan');
    }
  };

  const uploadFloorPlanFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await api.post('/admin/beacons/floor-plan/upload', {
          imageBase64: reader.result,
          imageMimeType: file.type,
        });
        setFloorPlanInput(res.data.url);
        toast.success('Floor plan uploaded');
        fetchHeatmap(windowMin);
      } catch (err) {
        toast.error(err.response?.data?.error || 'Upload failed');
      }
    };
    reader.readAsDataURL(file);
  };

  // Wallet pass arrival-card config
  const [passCfg, setPassCfg] = useState(null);
  const [passSaving, setPassSaving] = useState(false);
  const fetchPassConfig = async () => {
    try {
      const res = await api.get('/admin/wallet/pass-config');
      setPassCfg(res.data.config);
    } catch (e) {
      toast.error('Failed to load arrival config');
    }
  };
  const savePassConfig = async () => {
    setPassSaving(true);
    try {
      const res = await api.put('/admin/wallet/pass-config', passCfg);
      setPassCfg(res.data.config);
      toast.success('Saved. Re-push passes to apply to existing cardholders.');
    } catch (e) {
      toast.error('Failed to save arrival config');
    } finally {
      setPassSaving(false);
    }
  };
  const [heatLoading, setHeatLoading] = useState(false);
  const [repushing, setRepushing] = useState(false);

  const handleRepushPasses = async () => {
    if (!confirm('Re-push all Apple Wallet passes so existing cardholders get the beacon-enabled (lock-screen arrival) version?')) return;
    setRepushing(true);
    try {
      const res = await api.post('/admin/wallet/repush-passes');
      toast.success(`Re-pushed ${res.data.notified ?? 0} pass device(s)`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to re-push passes');
    } finally {
      setRepushing(false);
    }
  };

  const fetchBeacons = async () => {
    try {
      const res = await api.get('/admin/beacons');
      setBeacons(res.data.beacons || []);
    } catch (err) {
      toast.error('Failed to load beacons');
    } finally {
      setLoading(false);
    }
  };

  const fetchHeatmap = async (minutes = windowMin) => {
    setHeatLoading(true);
    try {
      const res = await api.get('/admin/beacons/heatmap', { params: { minutes } });
      setHeatmap(res.data);
      setFloorPlanInput(res.data.floorPlanUrl || '');
    } catch (err) {
      toast.error('Failed to load heatmap');
    } finally {
      setHeatLoading(false);
    }
  };

  useEffect(() => { fetchBeacons(); }, []);
  useEffect(() => { if (tab === 'heatmap') fetchHeatmap(windowMin); }, [tab, windowMin]);
  useEffect(() => { if (tab === 'arrival' && !passCfg) fetchPassConfig(); }, [tab]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setShowModal(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      uuid: b.uuid, major: String(b.major), minor: String(b.minor), label: b.label, type: b.type,
      linkedTableId: b.linkedTableId || '', zoneName: b.zoneName || '',
      floorX: b.floorX != null ? String(b.floorX) : '', floorY: b.floorY != null ? String(b.floorY) : '',
      txPower: b.txPower != null ? String(b.txPower) : '', isActive: b.isActive,
      notifyEnabled: !!b.notifyEnabled, notifyTitle: b.notifyTitle || '', notifyBody: b.notifyBody || '',
      notifyMinProximity: b.notifyMinProximity || 'near',
      notifyCooldownMinutes: b.notifyCooldownMinutes != null ? String(b.notifyCooldownMinutes) : '240',
      vipCooldownMinutes: b.vipCooldownMinutes != null ? String(b.vipCooldownMinutes) : '',
      positioningMode: b.positioningMode || (b.useForPositioning === false ? 'off' : 'full'),
      coverageFloorX: b.coverageFloorX != null ? String(b.coverageFloorX) : '',
      coverageFloorY: b.coverageFloorY != null ? String(b.coverageFloorY) : '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.uuid.trim() || form.major === '' || form.minor === '' || !form.label.trim()) {
      toast.error('UUID, Major, Minor, and Label are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        uuid: form.uuid.trim(),
        major: Number(form.major),
        minor: Number(form.minor),
        label: form.label.trim(),
        type: form.type,
        linkedTableId: form.linkedTableId.trim() || null,
        zoneName: form.zoneName.trim() || null,
        floorX: form.floorX === '' ? null : Number(form.floorX),
        floorY: form.floorY === '' ? null : Number(form.floorY),
        txPower: form.txPower === '' ? null : Number(form.txPower),
        isActive: form.isActive,
        notifyEnabled: form.notifyEnabled,
        notifyTitle: form.notifyTitle.trim() || null,
        notifyBody: form.notifyBody.trim() || null,
        notifyMinProximity: form.notifyMinProximity,
        notifyCooldownMinutes: form.notifyCooldownMinutes === '' ? 240 : Number(form.notifyCooldownMinutes),
        vipCooldownMinutes: form.vipCooldownMinutes === '' ? null : Number(form.vipCooldownMinutes),
        positioningMode: form.positioningMode,
        coverageFloorX: form.coverageFloorX === '' ? null : Number(form.coverageFloorX),
        coverageFloorY: form.coverageFloorY === '' ? null : Number(form.coverageFloorY),
      };
      if (editing) {
        await api.put(`/admin/beacons/${editing.id}`, payload);
        toast.success('Beacon updated');
      } else {
        await api.post('/admin/beacons', payload);
        toast.success('Beacon registered');
      }
      setShowModal(false);
      fetchBeacons();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save beacon');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (b) => {
    if (!confirm(`Delete "${b.label}"? This also removes its heatmap history.`)) return;
    try {
      await api.delete(`/admin/beacons/${b.id}`);
      toast.success('Beacon deleted');
      fetchBeacons();
    } catch (err) {
      toast.error('Failed to delete beacon');
    }
  };

  const maxPlayers = heatmap ? Math.max(1, ...heatmap.beacons.map((b) => b.uniquePlayers)) : 1;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Radio className="text-amber-500" size={28} />
          <h1 className="text-2xl font-bold text-white">Beacons</h1>
        </div>
        {tab === 'beacons' ? (
          <div className="flex items-center gap-2">
            <button onClick={handleRepushPasses} disabled={repushing} className="flex items-center gap-2 bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-600 disabled:opacity-50" title="Re-push Apple Wallet passes so existing cardholders get lock-screen arrival">
              <RefreshCw size={16} className={repushing ? 'animate-spin' : ''} /> Re-push Passes
            </button>
            <button onClick={openCreate} className="flex items-center gap-2 bg-amber-500 text-black font-semibold px-4 py-2 rounded-lg hover:bg-amber-400">
              <Plus size={18} /> Register Beacon
            </button>
          </div>
        ) : (
          <button onClick={() => fetchHeatmap(windowMin)} className="flex items-center gap-2 bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-600">
            <RefreshCw size={16} className={heatLoading ? 'animate-spin' : ''} /> Refresh
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-6">
        {['beacons', 'heatmap', 'arrival'].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-lg font-medium ${tab === t ? 'bg-amber-500 text-black' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
          >
            {t === 'beacons' ? 'Registered' : t === 'heatmap' ? 'Heatmap' : 'Arrival Card'}
          </button>
        ))}
      </div>

      {tab === 'beacons' && (
        <div className="bg-gray-800 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-400">Loading…</div>
          ) : beacons.length === 0 ? (
            <div className="p-12 text-center text-gray-400">
              <Radio className="mx-auto mb-3 opacity-50" size={40} />
              <p>No beacons registered yet.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Label</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Major / Minor</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Link</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Status</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {beacons.map((b) => (
                  <tr key={b.id} className={`hover:bg-gray-750 ${!b.isActive ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-white">{b.label}</div>
                      <div className="text-xs text-gray-500 font-mono">{b.uuid}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${TYPE_COLORS[b.type] || 'bg-gray-500/20 text-gray-300'}`}>{b.type}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-300 font-mono text-sm">{b.major} / {b.minor}</td>
                    <td className="px-4 py-3 text-gray-400 text-sm">{b.linkedTableId || b.zoneName || '—'}</td>
                    <td className="px-4 py-3 text-sm">
                      {b.isActive ? <span className="text-green-400">Active</span> : <span className="text-gray-500">Inactive</span>}
                      <div className="mt-1 flex items-center gap-2">
                        {(() => {
                          const h = b.health || (b.recentSightings > 0 ? 'weak' : 'silent');
                          const map = { healthy: ['#22c55e', 'Live'], weak: ['#eab308', 'Weak'], silent: ['#ef4444', 'No signal'] };
                          const [color, label] = map[h] || map.silent;
                          return (
                            <span title={`${b.recentSightings ?? 0} valid sightings in last 30 min`} className="inline-flex items-center gap-1 text-xs" style={{ color }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
                              {label}
                            </span>
                          );
                        })()}
                        {b.txPower == null && <span className="text-[10px] text-amber-400" title="No txPower set — calibrate for accurate positioning">uncalibrated</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(b)} className="p-2 text-amber-500 hover:bg-amber-500/20 rounded-lg" title="Edit"><Pencil size={16} /></button>
                        <button onClick={() => handleDelete(b)} className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg" title="Delete"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'heatmap' && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            {WINDOWS.map((w) => (
              <button
                key={w.minutes}
                onClick={() => setWindowMin(w.minutes)}
                className={`px-3 py-1.5 rounded-lg text-sm ${windowMin === w.minutes ? 'bg-amber-500 text-black' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
              >
                {w.label}
              </button>
            ))}
            {heatmap && (
              <span
                className="ml-auto text-sm text-gray-400 flex items-center gap-1"
                title={heatmap.locatablePlayers != null && heatmap.locatablePlayers < heatmap.totalUniquePlayers
                  ? `${heatmap.totalUniquePlayers - heatmap.locatablePlayers} player(s) were detected only by unplaced or "Off" beacons, so they can't be plotted on the map.`
                  : undefined}
              >
                <Activity size={14} /> {heatmap.totalUniquePlayers} on floor
                {heatmap.locatablePlayers != null && heatmap.locatablePlayers < heatmap.totalUniquePlayers && (
                  <span className="text-gray-500"> · {heatmap.locatablePlayers} on map</span>
                )}
              </span>
            )}
          </div>

          {/* View toggle + floor-plan URL */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {['bars', 'map'].map((v) => (
              <button key={v} onClick={() => setHeatView(v)} className={`px-3 py-1.5 rounded-lg text-sm capitalize ${heatView === v ? 'bg-amber-500 text-black' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}>{v}</button>
            ))}
            {heatView === 'map' && (
              <div className="flex items-center gap-2 ml-auto">
                <input
                  className="px-3 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm w-72"
                  value={floorPlanInput}
                  onChange={(e) => setFloorPlanInput(e.target.value)}
                  placeholder="Floor-plan image URL (optional)"
                />
                <button onClick={saveFloorPlan} className="px-3 py-1.5 bg-gray-700 text-white rounded-lg text-sm hover:bg-gray-600">Save</button>
                <label className="px-3 py-1.5 bg-amber-500 text-black rounded-lg text-sm font-medium cursor-pointer hover:bg-amber-400">
                  Upload
                  <input type="file" accept="image/*" onChange={uploadFloorPlanFile} className="hidden" />
                </label>
              </div>
            )}
          </div>

          <div className="bg-gray-800 rounded-xl p-4">
            {heatLoading ? (
              <div className="p-8 text-center text-gray-400">Loading…</div>
            ) : !heatmap || heatmap.beacons.length === 0 ? (
              <div className="p-8 text-center text-gray-400">No beacon activity in this window.</div>
            ) : heatView === 'bars' ? (
              <div className="space-y-3">
                {heatmap.beacons.map((b) => (
                  <div key={b.id} className="flex items-center gap-3">
                    <div className="w-40 truncate text-sm text-white">{b.label}</div>
                    <div className="flex-1 bg-gray-700 rounded h-6 overflow-hidden">
                      <div
                        className="h-6 bg-gradient-to-r from-amber-600 to-red-500 rounded flex items-center justify-end pr-2"
                        style={{ width: `${Math.max(4, (b.uniquePlayers / maxPlayers) * 100)}%` }}
                      >
                        <span className="text-xs font-bold text-black">{b.uniquePlayers}</span>
                      </div>
                    </div>
                    <div className="w-16 text-right text-xs text-gray-500">{b.sightings} pings</div>
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <div className="flex gap-2 mb-3">
                  {['density', 'positions', 'place'].map((m) => (
                    <button key={m} onClick={() => setMapMode(m)} className={`px-3 py-1 rounded text-xs ${mapMode === m ? 'bg-amber-500 text-black' : 'bg-gray-700 text-gray-300'}`}>
                      {m === 'place' ? 'Place beacons' : m === 'positions' ? 'Live positions' : 'Density'}
                    </button>
                  ))}
                </div>
                {mapMode === 'place' ? (
                  <FloorPlacer beacons={heatmap.beacons} floorPlanUrl={heatmap.floorPlanUrl} placeId={placeId} setPlaceId={setPlaceId} onPlace={placeBeacon} onPlaceCoverage={placeCoverageTarget} />
                ) : mapMode === 'positions' ? (
                  <FloorPositions beacons={heatmap.beacons} floorPlanUrl={heatmap.floorPlanUrl} positions={positions} />
                ) : (
                  <FloorHeatmap beacons={heatmap.beacons} maxPlayers={maxPlayers} floorPlanUrl={heatmap.floorPlanUrl} points={heatmap.densityPoints} />
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'arrival' && (
        <div className="bg-gray-800 rounded-xl p-5 max-w-xl">
          {!passCfg ? (
            <div className="text-center text-gray-400 p-6">Loading…</div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-400">Controls when a member's Apple Wallet card surfaces on their lock screen on arrival.</p>

              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" checked={passCfg.useLocation} onChange={(e) => setPassCfg({ ...passCfg, useLocation: e.target.checked })} className="h-4 w-4 rounded" />
                GPS location trigger (shows the card as they approach the casino)
              </label>
              {passCfg.useLocation && (
                <div className="grid grid-cols-2 gap-3 pl-6">
                  <Field label="Latitude"><input className={inp} type="number" value={passCfg.lat} onChange={(e) => setPassCfg({ ...passCfg, lat: e.target.value })} /></Field>
                  <Field label="Longitude"><input className={inp} type="number" value={passCfg.lng} onChange={(e) => setPassCfg({ ...passCfg, lng: e.target.value })} /></Field>
                </div>
              )}

              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" checked={passCfg.useBeacons} onChange={(e) => setPassCfg({ ...passCfg, useBeacons: e.target.checked })} className="h-4 w-4 rounded" />
                iBeacon trigger (precise, works indoors — uses your active beacons)
              </label>

              <Field label="Lock-screen text"><input className={inp} value={passCfg.relevantText} onChange={(e) => setPassCfg({ ...passCfg, relevantText: e.target.value })} placeholder="Welcome to EDH Casino!" /></Field>
              <Field label="Pass icon URL (optional override, PNG)"><input className={inp} value={passCfg.iconUrl || ''} onChange={(e) => setPassCfg({ ...passCfg, iconUrl: e.target.value })} placeholder="https://…" /></Field>

              <div className="flex items-center gap-3 pt-1">
                <button onClick={savePassConfig} disabled={passSaving} className="px-4 py-2 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50">{passSaving ? 'Saving…' : 'Save'}</button>
                <button onClick={handleRepushPasses} disabled={repushing} className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50">Re-push to existing passes</button>
              </div>
              <p className="text-xs text-gray-500">New downloads apply immediately; "Re-push" updates cards already in members' wallets.</p>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-white">{editing ? 'Edit Beacon' : 'Register Beacon'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white"><X size={22} /></button>
            </div>
            <div className="space-y-3">
              <Field label="Label *"><input className={inp} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g., Main Entrance" /></Field>
              <Field label="UUID *"><input className={`${inp} font-mono text-sm`} value={form.uuid} onChange={(e) => setForm({ ...form, uuid: e.target.value })} placeholder="xxxxxxxx-xxxx-…" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Major *"><input className={inp} type="number" value={form.major} onChange={(e) => setForm({ ...form, major: e.target.value })} /></Field>
                <Field label="Minor *"><input className={inp} type="number" value={form.minor} onChange={(e) => setForm({ ...form, minor: e.target.value })} /></Field>
              </div>
              <Field label="Type">
                <div className="flex flex-wrap gap-2">
                  {TYPES.map((t) => (
                    <button key={t} onClick={() => setForm({ ...form, type: t })} className={`px-3 py-1.5 rounded text-sm ${form.type === t ? 'bg-amber-500 text-black' : 'bg-gray-700 text-gray-300'}`}>{t}</button>
                  ))}
                </div>
              </Field>
              {form.type === 'entrance' && (
                <Field label="VIP alert cooldown (min) — blank = default 6h · 0 = every visit (demo)">
                  <input className={inp} type="number" value={form.vipCooldownMinutes} onChange={(e) => setForm({ ...form, vipCooldownMinutes: e.target.value })} placeholder="default 360" />
                </Field>
              )}
              <Field label="Linked Table ID (optional)"><input className={inp} value={form.linkedTableId} onChange={(e) => setForm({ ...form, linkedTableId: e.target.value })} placeholder="TableCaptain PlayerId of a table" /></Field>
              <Field label="Zone name (optional)"><input className={inp} value={form.zoneName} onChange={(e) => setForm({ ...form, zoneName: e.target.value })} placeholder="e.g., Poker Room" /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Floor X"><input className={inp} type="number" value={form.floorX} onChange={(e) => setForm({ ...form, floorX: e.target.value })} /></Field>
                <Field label="Floor Y"><input className={inp} type="number" value={form.floorY} onChange={(e) => setForm({ ...form, floorY: e.target.value })} /></Field>
                <Field label="Tx Power (RSSI@1m)">
                  <div className="flex gap-1">
                    <input className={inp} type="number" value={form.txPower} onChange={(e) => setForm({ ...form, txPower: e.target.value })} placeholder="-59" />
                    {editing && (
                      <button
                        type="button"
                        onClick={handleCalibrate}
                        disabled={calibrating}
                        title="Stand ~1m from this beacon with the app open for ~30s, then click to auto-calibrate from recent signal."
                        className="px-2 py-1 text-xs bg-gray-600 text-amber-300 rounded hover:bg-gray-500 disabled:opacity-50 whitespace-nowrap"
                      >
                        {calibrating ? '…' : 'Calibrate @1m'}
                      </button>
                    )}
                  </div>
                </Field>
              </div>
              <Field label="Live-map positioning">
                <div className="flex gap-1">
                  {[
                    { v: 'full', label: 'Full anchor' },
                    { v: 'coverage', label: 'Coverage only' },
                    { v: 'off', label: 'Off' },
                  ].map((m) => (
                    <button
                      key={m.v}
                      type="button"
                      onClick={() => setForm({ ...form, positioningMode: m.v })}
                      className={`flex-1 px-2 py-1.5 rounded text-xs ${form.positioningMode === m.v ? 'bg-amber-500 text-black' : 'bg-gray-700 text-gray-300'}`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                <span className="block text-xs text-gray-500 mt-1">
                  {form.positioningMode === 'full' && 'Normal anchor — can place a player at this beacon’s spot.'}
                  {form.positioningMode === 'coverage' && 'Helps triangulate a nearby walkable area but can never dominate/attract players to this beacon’s spot. Tip: set its “coverage target” on the map (Heatmap → Place beacons → Coverage target) so its density lands on the real walkable spot instead of the beacon’s corner.'}
                  {form.positioningMode === 'off' && 'Fully excluded from positioning (still reports sightings & health). Use for beacons in physically inaccessible corners.'}
                </span>
              </Field>
              {/* Proximity notification */}
              <div className="border-t border-gray-700 pt-3">
                <label className="flex items-center gap-2 text-sm text-gray-300">
                  <input type="checkbox" checked={form.notifyEnabled} onChange={(e) => setForm({ ...form, notifyEnabled: e.target.checked })} className="h-4 w-4 rounded" />
                  Send a notification when a player is nearby
                </label>
                {form.notifyEnabled && (
                  <div className="mt-3 space-y-3">
                    <Field label="Notification title"><input className={inp} value={form.notifyTitle} onChange={(e) => setForm({ ...form, notifyTitle: e.target.value })} placeholder={form.label || 'e.g., High Limit Room'} /></Field>
                    <Field label="Notification message"><textarea className={`${inp} h-20`} value={form.notifyBody} onChange={(e) => setForm({ ...form, notifyBody: e.target.value })} placeholder="e.g., Stop by for a complimentary drink!" /></Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Trigger proximity">
                        <div className="flex gap-1">
                          {PROXIMITIES.map((p) => (
                            <button key={p} onClick={() => setForm({ ...form, notifyMinProximity: p })} className={`flex-1 px-2 py-1.5 rounded text-xs ${form.notifyMinProximity === p ? 'bg-amber-500 text-black' : 'bg-gray-700 text-gray-300'}`}>{p}</button>
                          ))}
                        </div>
                      </Field>
                      <Field label="Cooldown (min)"><input className={inp} type="number" value={form.notifyCooldownMinutes} onChange={(e) => setForm({ ...form, notifyCooldownMinutes: e.target.value })} /></Field>
                    </div>
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} className="h-4 w-4 rounded" />
                Active
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-600">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2.5 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50">
                {saving ? 'Saving…' : editing ? 'Save' : 'Register'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = 'w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-500';
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

// 256-entry cool->hot color ramp (blue -> cyan -> green -> yellow -> red).
function heatRamp() {
  const stops = [
    [0.0, [37, 99, 235]],   // blue
    [0.3, [6, 182, 212]],   // cyan
    [0.5, [34, 197, 94]],   // green
    [0.7, [234, 179, 8]],   // yellow
    [0.85, [249, 115, 22]], // orange
    [1.0, [239, 68, 68]],   // red
  ];
  const lut = [];
  for (let i = 0; i < 256; i++) {
    const t = i / 255;
    let s = stops[0], e = stops[stops.length - 1];
    for (let k = 0; k < stops.length - 1; k++) {
      if (t >= stops[k][0] && t <= stops[k + 1][0]) { s = stops[k]; e = stops[k + 1]; break; }
    }
    const span = (e[0] - s[0]) || 1;
    const f = (t - s[0]) / span;
    lut.push([
      Math.round(s[1][0] + (e[1][0] - s[1][0]) * f),
      Math.round(s[1][1] + (e[1][1] - s[1][1]) * f),
      Math.round(s[1][2] + (e[1][2] - s[1][2]) * f),
    ]);
  }
  return lut;
}
const HEAT_LUT = heatRamp();

function FloorHeatmap({ beacons, maxPlayers, floorPlanUrl, points = [] }) {
  const [aspect, setAspect] = useState(16 / 10);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const plotted = beacons.filter((b) => b.floorX != null && b.floorY != null);
  const unplaced = beacons.length - plotted.length;
  const hasPoints = Array.isArray(points) && points.length > 0;

  // Canvas heatmap: accumulate soft intensity per point (additive), then map the
  // accumulated alpha through a cool->hot color ramp so density reads clearly
  // instead of a flat red blob.
  useEffect(() => {
    const canvas = canvasRef.current;
    const cont = containerRef.current;
    if (!canvas || !cont) return;
    const w = cont.clientWidth, h = cont.clientHeight;
    if (!w || !h) return;
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, w, h);
    if (!hasPoints) return;

    // 1) Accumulate intensity (grayscale alpha) with a tight, smooth falloff.
    const R = Math.max(16, Math.round(Math.min(w, h) * 0.05)); // tighter hotspots
    ctx.globalCompositeOperation = 'lighter';
    for (const p of points) {
      const x = Math.max(0, Math.min(1, p.x)) * w;
      const y = Math.max(0, Math.min(1, p.y)) * h;
      const g = ctx.createRadialGradient(x, y, 0, x, y, R);
      g.addColorStop(0, 'rgba(0,0,0,0.20)');
      g.addColorStop(0.5, 'rgba(0,0,0,0.08)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(x, y, R, 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalCompositeOperation = 'source-over';

    // 1b) Blur the accumulated field for a smooth, continuous gradient.
    try {
      const tmp = document.createElement('canvas');
      tmp.width = w; tmp.height = h;
      const tctx = tmp.getContext('2d');
      tctx.filter = 'blur(5px)';
      tctx.drawImage(canvas, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(tmp, 0, 0);
    } catch (e) { /* filter unsupported -> unblurred still fine */ }

    // 2) Normalize to the busiest spot, then colorize through the ramp so the
    // full blue->red range is always used (dense areas no longer clip to solid
    // red, and relative hot/cool contrast holds at any player count).
    const img = ctx.getImageData(0, 0, w, h);
    const d = img.data;
    let maxA = 0;
    for (let i = 3; i < d.length; i += 4) { if (d[i] > maxA) maxA = d[i]; }
    if (maxA === 0) return;
    for (let i = 0; i < d.length; i += 4) {
      const a = d[i + 3];
      if (a === 0) continue;
      const t = a / maxA;                        // 0..1 relative density
      const c = HEAT_LUT[Math.min(255, Math.round(t * 255))];
      d[i] = c[0]; d[i + 1] = c[1]; d[i + 2] = c[2];
      d[i + 3] = Math.min(215, Math.round(30 + t * 190));
    }
    ctx.putImageData(img, 0, 0);
  }, [points, aspect, hasPoints]);

  return (
    <div>
      <div
        ref={containerRef}
        className="relative w-full mx-auto rounded-lg overflow-hidden border border-gray-700"
        style={{
          maxWidth: 680,
          aspectRatio: String(aspect),
          background: floorPlanUrl ? '#0b0d12' : 'repeating-linear-gradient(45deg, #15171c, #15171c 12px, #181b21 12px, #181b21 24px)',
        }}
      >
        {floorPlanUrl && (
          <img src={floorPlanUrl} alt="Floor plan" onLoad={(e) => { const im = e.currentTarget; if (im.naturalWidth) setAspect(im.naturalWidth / im.naturalHeight); }} className="absolute inset-0 w-full h-full object-cover opacity-90" />
        )}
        {/* Canvas heat layer (triangulated player density, cool->hot). */}
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }} />
        {/* Beacons as small faint reference markers. */}
        {plotted.map((b) => (
          <div key={b.id} title={`${b.label}: ${b.uniquePlayers} pings`} style={{
            position: 'absolute',
            left: `${Math.max(0, Math.min(1, Number(b.floorX))) * 100}%`,
            top: `${Math.max(0, Math.min(1, Number(b.floorY))) * 100}%`,
            width: 7, height: 7, transform: 'translate(-50%, -50%)',
            borderRadius: '50%', background: 'rgba(226,232,240,0.85)', border: '1px solid rgba(15,23,42,0.7)',
          }} />
        ))}
        {/* Fallback bubbles only when nothing could be triangulated. */}
        {!hasPoints && plotted.map((b) => {
          const intensity = Math.min(1, b.uniquePlayers / maxPlayers);
          const size = 24 + intensity * 56;
          return (
            <div key={`fb-${b.id}`} className="absolute flex items-center justify-center rounded-full"
              title={`${b.label}: ${b.uniquePlayers} players`}
              style={{
                left: `${Math.max(0, Math.min(1, Number(b.floorX))) * 100}%`,
                top: `${Math.max(0, Math.min(1, Number(b.floorY))) * 100}%`,
                width: size, height: size, transform: 'translate(-50%, -50%)',
                background: `radial-gradient(circle, rgba(239,68,68,${0.2 + intensity * 0.6}) 0%, rgba(239,68,68,0) 70%)`,
              }}>
              <span className="text-[10px] font-bold text-white drop-shadow">{b.uniquePlayers || ''}</span>
            </div>
          );
        })}
      </div>
      {/* Legend + caption */}
      <div className="flex items-center justify-center gap-2 mt-3">
        <span className="text-[11px] text-gray-500">Low</span>
        <div className="h-2 w-40 rounded" style={{ background: 'linear-gradient(to right, #2563eb, #06b6d4, #22c55e, #eab308, #f97316, #ef4444)' }} />
        <span className="text-[11px] text-gray-500">High</span>
      </div>
      <p className="text-xs text-gray-500 mt-2 text-center">
        Triangulated player density over the window; grey dots are beacons.
        {unplaced > 0 && ` ${unplaced} beacon${unplaced !== 1 ? 's' : ''} not placed.`}
      </p>
    </div>
  );
}

function FloorPlacer({ beacons, floorPlanUrl, placeId, setPlaceId, onPlace, onPlaceCoverage }) {
  const [aspect, setAspect] = useState(16 / 10);
  // 'beacon' places the beacon's own spot; 'coverage' places the coverage target
  // (only meaningful for coverage-only beacons).
  const [placeKind, setPlaceKind] = useState('beacon');
  if (!floorPlanUrl) {
    return <p className="text-sm text-gray-400 text-center p-6">Upload a floor-plan image first (the field above), then place beacons here.</p>;
  }
  const selected = beacons.find((b) => b.id === placeId);
  const isCoverage = selected && selected.positioningMode === 'coverage';
  const kind = isCoverage ? placeKind : 'beacon';
  const handleClick = (e) => {
    if (!placeId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    if (kind === 'coverage' && onPlaceCoverage) onPlaceCoverage(placeId, x, y);
    else onPlace(placeId, x, y);
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        {beacons.map((b) => {
          const placed = b.floorX != null && b.floorY != null;
          return (
            <button
              key={b.id}
              onClick={() => { setPlaceId(b.id); setPlaceKind('beacon'); }}
              className={`px-3 py-1.5 rounded text-sm flex items-center gap-1.5 ${placeId === b.id ? 'bg-amber-500 text-black' : 'bg-gray-700 text-gray-300'}`}
            >
              {placed && <span className="text-green-400 text-xs">●</span>}{b.label}
            </button>
          );
        })}
      </div>
      {isCoverage && (
        <div className="flex gap-1 mb-2 max-w-xs">
          {[
            { v: 'beacon', label: 'Beacon spot' },
            { v: 'coverage', label: 'Coverage target' },
          ].map((k) => (
            <button
              key={k.v}
              type="button"
              onClick={() => setPlaceKind(k.v)}
              className={`flex-1 px-2 py-1.5 rounded text-xs ${placeKind === k.v ? 'bg-sky-500 text-black' : 'bg-gray-700 text-gray-300'}`}
            >
              {k.label}
            </button>
          ))}
        </div>
      )}
      <p className="text-xs text-gray-400 mb-2">
        {!placeId
          ? 'Select a beacon above, then click its spot on the map.'
          : kind === 'coverage'
            ? `Click the walkable area "${selected?.label}" should represent (its coverage target).`
            : `Click the map to place "${selected?.label}".`}
      </p>
      <div
        className="relative mx-auto rounded-lg overflow-hidden border border-gray-700 cursor-crosshair"
        style={{ maxWidth: 680, width: '100%', aspectRatio: String(aspect) }}
        onClick={handleClick}
      >
        <img
          src={floorPlanUrl}
          alt="Floor plan"
          onLoad={(e) => { const im = e.currentTarget; if (im.naturalWidth) setAspect(im.naturalWidth / im.naturalHeight); }}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />
        {/* Coverage target markers (sky) with a dashed tie-line back to the beacon. */}
        {beacons.filter((b) => b.positioningMode === 'coverage' && b.coverageFloorX != null && b.coverageFloorY != null).map((b) => (
          <div key={`cov-${b.id}`} className="absolute pointer-events-none" style={{ inset: 0 }}>
            {b.floorX != null && b.floorY != null && (
              <svg className="absolute inset-0 w-full h-full" style={{ overflow: 'visible' }}>
                <line
                  x1={`${b.floorX * 100}%`} y1={`${b.floorY * 100}%`}
                  x2={`${b.coverageFloorX * 100}%`} y2={`${b.coverageFloorY * 100}%`}
                  stroke="rgba(56,189,248,0.7)" strokeWidth="1.5" strokeDasharray="4 3"
                />
              </svg>
            )}
            <div
              className="absolute flex flex-col items-center"
              style={{ left: `${b.coverageFloorX * 100}%`, top: `${b.coverageFloorY * 100}%`, transform: 'translate(-50%, -50%)' }}
            >
              <div className="w-3 h-3 rounded-full bg-sky-400 ring-2 ring-sky-300" />
              <span className="text-[9px] text-white bg-sky-900/70 px-1 rounded mt-0.5 whitespace-nowrap">{b.label} target</span>
            </div>
          </div>
        ))}
        {beacons.filter((b) => b.floorX != null && b.floorY != null).map((b) => (
          <div
            key={b.id}
            className="absolute flex flex-col items-center pointer-events-none"
            style={{ left: `${b.floorX * 100}%`, top: `${b.floorY * 100}%`, transform: 'translate(-50%, -50%)' }}
          >
            <div className={`w-3 h-3 rounded-full ${b.id === placeId ? 'bg-amber-400 ring-2 ring-amber-300' : b.positioningMode === 'coverage' ? 'bg-sky-500' : 'bg-red-500'}`} />
            <span className="text-[9px] text-white bg-black/60 px-1 rounded mt-0.5 whitespace-nowrap">{b.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FloorPositions({ beacons, floorPlanUrl, positions }) {
  const [aspect, setAspect] = useState(16 / 10);
  if (!floorPlanUrl) {
    return <p className="text-sm text-gray-400 text-center p-6">Upload a floor-plan image and place beacons to see live positions.</p>;
  }
  const placed = beacons.filter((b) => b.floorX != null && b.floorY != null);
  return (
    <div>
      <p className="text-xs text-gray-400 mb-2">{positions.length} player{positions.length !== 1 ? 's' : ''} located now (triangulated from nearby beacons, refreshes every few seconds).</p>
      <div className="relative mx-auto rounded-lg overflow-hidden border border-gray-700" style={{ maxWidth: 680, width: '100%', aspectRatio: String(aspect) }}>
        <img src={floorPlanUrl} alt="Floor plan" onLoad={(e) => { const im = e.currentTarget; if (im.naturalWidth) setAspect(im.naturalWidth / im.naturalHeight); }} className="absolute inset-0 w-full h-full object-cover" />
        {placed.map((b) => (
          <div key={b.id} className="absolute w-2 h-2 rounded-full bg-gray-400/60" style={{ left: `${b.floorX * 100}%`, top: `${b.floorY * 100}%`, transform: 'translate(-50%, -50%)' }} />
        ))}
        {/* Heat cloud: soft semi-transparent blobs that STACK, so clustered
            players read as hotspots. Plain alpha (no blend mode) stays visible
            over both light and dark floor plans. */}
        {positions.map((p, i) => (
          <div key={`heat-${i}`} style={{
            position: 'absolute', left: `${p.x * 100}%`, top: `${p.y * 100}%`,
            width: 96, height: 96, transform: 'translate(-50%, -50%)',
            borderRadius: '50%', pointerEvents: 'none',
            background: 'radial-gradient(circle, rgba(239,68,68,0.35) 0%, rgba(239,68,68,0.14) 45%, rgba(239,68,68,0) 72%)',
          }} />
        ))}
        {positions.map((p, i) => (
          <div key={i} className="absolute" style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%`, transform: 'translate(-50%, -50%)' }}>
            {/* glow ring + solid dot — visible on a white floor plan (the old
                screen-blended red was invisible over white) */}
            <div style={{ position: 'absolute', left: -22, top: -22, width: 44, height: 44, borderRadius: '50%', border: `2px solid ${accentRgba(0.55)}`, boxShadow: `0 0 14px ${accentRgba(0.7)}` }} />
            <div style={{ position: 'absolute', left: -8, top: -8, width: 16, height: 16, borderRadius: '50%', background: ACCENT, border: '2px solid #111827' }} />
          </div>
        ))}
      </div>
      {positions.length === 0 && (
        <p className="text-xs text-gray-500 mt-3 text-center">No players located right now. Positions need players with the app foregrounded near placed beacons.</p>
      )}
    </div>
  );
}
